import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  draftFromSections,
  draftRowSourceIds,
  dropInFlatList,
  duplicateInDraft,
  duplicateRowInDraft,
  findRow,
  flatItemKey,
  flattenDraft,
  holdsOriginalRow,
  isDraftCopy,
  isRowCopy,
  mergeRowIntoPrevious,
  moveIdBy,
  previousRowKey,
  removeFromDraft,
  removeRowFromDraft,
  rowHasContent,
  sectionHasContent,
  visibleDraft,
  visibleRows,
  type ChartDraft,
  type FlatDraftItem,
  type RowDraftEntry,
} from '@chordbaker/music-theory';
import { createPortal } from 'react-dom';
import { RowState, SectionState } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import SectionEditorModalFrame from './SectionEditorModalFrame';
import ConfirmationModal from './ConfirmationModal';
import { RowItem, SectionHeaderItem } from './ChartReorderItems';
import { createReorderStyles, rowText, titleOf } from './chartReorderShared';

const LAST_ROW_NOTE = 'A section needs at least one row';
/** How long the note above stays up. */
const NOTE_MS = 3000;

export interface ChartReorderModalProps {
  isOpen: boolean;
  sections: SectionState[];
  onClose: () => void;
  /** The draft: sections and rows in their new order, copies to create, ones to delete. */
  onApply: (draft: ChartDraft) => void;
}

type ListItem = FlatDraftItem & { id: string };

/**
 * "Reorder chart" (header): the song's sections as a compact list of their
 * headers (title, repeat, row count); a section's title area shows its rows,
 * one line of chords each. Like the React Native sheet, the chart never drags:
 * sections and rows are one sortable list (`flattenDraft`), so a row can be
 * dragged into another section, and where it lands is worked out after the
 * drop (`dropInFlatList`); a move that would empty a section is refused. Each
 * section and row can be duplicated (the copy appears right under it) or
 * deleted (one with content asks first: the web has no undo). Everything is a
 * draft until Apply. Keyboard users focus a grip, press Space to pick the item
 * up, move it with the arrow keys and press Space to drop it; each step is
 * announced.
 */
const ChartReorderModal: React.FC<ChartReorderModalProps> = ({
  isOpen,
  sections,
  onClose,
  onApply,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createReorderStyles(theme), [theme]);

  const [draft, setDraft] = useState<ChartDraft>([]);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [status, setStatus] = useState('');
  const [note, setNote] = useState<string | null>(null);
  // The item whose delete is being confirmed; a row can keep its chords instead
  const [confirmingDelete, setConfirmingDelete] = useState<{
    remove: () => void;
    keepChords?: () => void;
    title: string;
    message: string;
  } | null>(null);
  const copyCountRef = useRef(0);
  const newKey = () => `copy-${++copyCountRef.current}`;
  useEffect(() => {
    if (isOpen) {
      setDraft(draftFromSections(sections));
      setExpanded(new Set());
      setStatus('');
      setNote(null);
      setConfirmingDelete(null);
    }
    // Only on open: the draft must not reset when the chart changes behind the
    // sheet (applying the draft keeps such edits)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  useEffect(() => {
    if (!note) return;
    const timer = setTimeout(() => setNote(null), NOTE_MS);
    return () => clearTimeout(timer);
  }, [note]);

  const sectionsById = useMemo(() => new Map(sections.map(section => [section.id, section])), [sections]);
  const rowsById = useMemo(
    () => new Map(sections.flatMap(section => section.rows.map(row => [row.id, row] as const))),
    [sections],
  );
  const shownSections = useMemo(() => visibleDraft(draft), [draft]);
  // Entries whose section or row went away while the sheet was open aren't shown
  const items = useMemo<ListItem[]>(
    () =>
      flattenDraft(draft, expanded)
        .filter(item => {
          if (item.type === 'section') {
            return sectionsById.has(shownSections.find(s => s.key === item.key)?.sourceId ?? '');
          }
          const at = findRow(draft, item.key);
          return !!at && rowsById.has(visibleRows(draft[at.sectionIndex])[at.rowIndex].sourceRowId);
        })
        .map(item => ({ ...item, id: flatItemKey(item) })),
    [draft, expanded, sectionsById, rowsById, shownSections],
  );
  const ids = useMemo(() => items.map(item => item.id), [items]);
  const itemById = (id: UniqueIdentifier) => items.find(item => item.id === String(id));

  const sectionTitleOf = (key: string, from: ChartDraft = draft) =>
    titleOf(sectionsById.get(from.find(s => s.key === key)?.sourceId ?? ''));
  /** The row as Apply will build it: rows merged into it add their bars. */
  const draftRow = (entry: RowDraftEntry): RowState | undefined => {
    const source = rowsById.get(entry.sourceRowId);
    if (!source || !entry.merged?.length) return source;
    return { ...source, bars: draftRowSourceIds(entry).flatMap(id => rowsById.get(id)?.bars ?? []) };
  };
  const rowOf = (key: string, from: ChartDraft = draft): RowState | undefined => {
    const at = findRow(from, key);
    return at ? draftRow(visibleRows(from[at.sectionIndex])[at.rowIndex]) : undefined;
  };

  const sensors = useSensors(
    // A few pixels of travel, so a click on an item doesn't count as a drag
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Escape during a keyboard move cancels the move; the frame's own Escape
  // handler runs first (React, before dnd-kit's window listener), so it must
  // not close the sheet while an item is held
  const draggingRef = useRef(false);
  const handleClose = () => {
    if (!draggingRef.current) onClose();
  };

  const expand = (key: string) => setExpanded(prev => (prev.has(key) ? prev : new Set(prev).add(key)));

  /** The list after moving `activeId` over `overId`, as dnd-kit's sortable would lay it out. */
  const reordered = (activeId: UniqueIdentifier, overId: UniqueIdentifier) => {
    const order = moveIdBy(ids, String(activeId), ids.indexOf(String(overId)) - ids.indexOf(String(activeId)));
    return order.map(id => itemById(id)!);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    draggingRef.current = false;
    const moved = itemById(active.id);
    if (!over || active.id === over.id || !moved) return;
    const result = dropInFlatList(draft, expanded, reordered(active.id, over.id), moved);
    if (result.refused) {
      setNote(LAST_ROW_NOTE);
      return;
    }
    setDraft(result.draft);
    if (result.landedSectionKey) expand(result.landedSectionKey);
  };

  const handleDuplicateSection = (key: string) => {
    const next = duplicateInDraft(draft, key, newKey);
    setDraft(next);
    const position = visibleDraft(draft).findIndex(s => s.key === key) + 2;
    setStatus(`Copy of ${sectionTitleOf(key)} added at position ${position} of ${visibleDraft(next).length}.`);
  };

  const handleDuplicateRow = (key: string) => {
    const at = findRow(draft, key);
    if (!at) return;
    setDraft(duplicateRowInDraft(draft, key, newKey));
    setStatus(`Copy added as row ${at.rowIndex + 2} of ${sectionTitleOf(draft[at.sectionIndex].key)}.`);
  };

  // The chart always keeps one section, and a section one row. One with
  // chords, lyrics or a note asks first, on the click rather than at Apply,
  // so several deletions never pile up behind one confirmation; a copy has
  // nothing to lose
  const handleDeleteSection = (key: string) => {
    const entry = draft.find(e => e.key === key);
    const section = sectionsById.get(entry?.sourceId ?? '');
    if (!entry || !section || shownSections.length <= 1) return;
    const remove = () => {
      setStatus(`${titleOf(section)} deleted.`);
      setDraft(prev => removeFromDraft(prev, key));
    };
    if (isDraftCopy(entry) || !sectionHasContent(section)) remove();
    else setConfirmingDelete({
      remove,
      title: 'Delete section',
      message: `Delete "${titleOf(section)}" and everything in it? Once you apply, this can't be undone.`,
    });
  };

  const handleDeleteRow = (key: string) => {
    const at = findRow(draft, key);
    const row = rowOf(key);
    if (!at || !row) return;
    const entry = visibleRows(draft[at.sectionIndex])[at.rowIndex];
    const section = sectionTitleOf(draft[at.sectionIndex].key);
    const remove = () => {
      setStatus(`Row deleted from ${section}.`);
      setDraft(prev => removeRowFromDraft(prev, key).draft);
    };
    // As on the chart, a row's chords can go to the end of the row above
    // instead (or the last row of the section above)
    const targetKey = previousRowKey(draft, key);
    const keepChords = targetKey
      ? () => {
          const next = mergeRowIntoPrevious(draft, key).draft;
          const target = findRow(next, targetKey);
          if (next === draft || !target) return;
          const targetSection = next[target.sectionIndex];
          setDraft(next);
          setExpanded(prev => new Set(prev).add(targetSection.key));
          setStatus(`Chords moved to row ${target.rowIndex + 1} of ${sectionTitleOf(targetSection.key, next)}.`);
        }
      : undefined;
    // A copy with nothing of the chart's in it has nothing to lose
    if (!holdsOriginalRow(entry) || !rowHasContent(row)) remove();
    else setConfirmingDelete({
      remove,
      keepChords,
      title: 'Delete row',
      message: keepChords
        ? `Delete row ${at.rowIndex + 1} of "${section}" (${rowText(row)}), or keep its chords by moving them to the end of the previous row? Once you apply, this can't be undone.`
        : `Delete row ${at.rowIndex + 1} of "${section}" (${rowText(row)})? Once you apply, this can't be undone.`,
    });
  };

  /** What a drop at `over` would do, for the screen-reader announcements. */
  const describeDrop = (activeId: UniqueIdentifier, overId: UniqueIdentifier, verb: string) => {
    const moved = itemById(activeId);
    if (!moved) return undefined;
    if (moved.type === 'section') {
      const order = reordered(activeId, overId).filter(item => item.type === 'section');
      return `${sectionTitleOf(moved.key)} ${verb} position ${order.findIndex(item => item.key === moved.key) + 1} of ${order.length}.`;
    }
    const result = dropInFlatList(draft, expanded, reordered(activeId, overId), moved);
    if (result.refused) return `${LAST_ROW_NOTE}.`;
    const at = findRow(result.draft, moved.key);
    if (!at) return undefined;
    const section = result.draft[at.sectionIndex];
    return `Row ${verb} ${sectionTitleOf(section.key, result.draft)}, row ${at.rowIndex + 1} of ${visibleRows(section).length}.`;
  };
  const nameOf = (id: UniqueIdentifier) => {
    const item = itemById(id);
    if (!item) return '';
    if (item.type === 'section') return sectionTitleOf(item.key);
    const row = rowOf(item.key);
    return row ? `the row ${rowText(row)}` : 'the row';
  };
  // dnd-kit reports the item over its own place right after the pick-up;
  // announce a move only when the place changes, so "Picked up" isn't cut off
  const lastOverRef = useRef<UniqueIdentifier | null>(null);
  const announcements: Announcements = {
    onDragStart: ({ active }) => {
      lastOverRef.current = active.id;
      return `Picked up ${nameOf(active.id)}.`;
    },
    onDragOver: ({ active, over }) => {
      if (!over || over.id === lastOverRef.current) return undefined;
      lastOverRef.current = over.id;
      return describeDrop(active.id, over.id, 'moved to');
    },
    onDragEnd: ({ active, over }) => (over ? describeDrop(active.id, over.id, 'dropped in') : undefined),
    onDragCancel: ({ active }) => `Move cancelled. ${nameOf(active.id)} stays where it was.`,
  };

  return (
    <>
      <SectionEditorModalFrame
        isOpen={isOpen}
        title="Reorder chart"
        subtitle="Drag to move. Open a section to move its rows."
        onClose={handleClose}
        onApply={() => onApply(draft)}
      >
        {note && <p role="status" style={styles.note} data-testid="section-reorder-note">{note}</p>}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          onDragStart={() => { draggingRef.current = true; }}
          onDragEnd={handleDragEnd}
          onDragCancel={() => { draggingRef.current = false; }}
          accessibility={{
            announcements,
            screenReaderInstructions: {
              draggable:
                'To move a section or row, press Space or Enter, use the arrow keys to move it, then press Space or Enter to drop it, or Escape to cancel.',
            },
          }}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <ul style={styles.list} aria-label="Sections and rows">
              {items.map(item => {
                if (item.type === 'section') {
                  const index = shownSections.findIndex(s => s.key === item.key);
                  const entry = shownSections[index];
                  const isExpanded = expanded.has(entry.key);
                  return (
                    <SectionHeaderItem
                      key={item.id}
                      id={item.id}
                      keyId={entry.key}
                      section={sectionsById.get(entry.sourceId)!}
                      isCopy={isDraftCopy(entry)}
                      rowCount={visibleRows(entry).length}
                      expanded={isExpanded}
                      position={index + 1}
                      count={shownSections.length}
                      canDelete={shownSections.length > 1}
                      onToggle={() =>
                        setExpanded(prev => {
                          const next = new Set(prev);
                          if (!next.delete(entry.key)) next.add(entry.key);
                          return next;
                        })
                      }
                      onDuplicate={() => handleDuplicateSection(entry.key)}
                      onDelete={() => handleDeleteSection(entry.key)}
                      styles={styles}
                    />
                  );
                }
                const at = findRow(draft, item.key)!;
                const siblings = visibleRows(draft[at.sectionIndex]);
                const entry = siblings[at.rowIndex];
                return (
                  <RowItem
                    key={item.id}
                    id={item.id}
                    keyId={entry.key}
                    row={draftRow(entry)!}
                    isCopy={isRowCopy(entry)}
                    sectionTitle={sectionTitleOf(draft[at.sectionIndex].key)}
                    position={at.rowIndex + 1}
                    canDelete={siblings.length > 1}
                    onDuplicate={() => handleDuplicateRow(entry.key)}
                    onDelete={() => handleDeleteRow(entry.key)}
                    styles={styles}
                  />
                );
              })}
            </ul>
          </SortableContext>
        </DndContext>
        <div role="status" style={styles.srOnly} data-testid="section-reorder-status">{status}</div>
      </SectionEditorModalFrame>
      {/* Outside the frame, so its events don't reach the sheet (Escape would close it);
          in document.body after the sheet, so it stacks above it */}
      {confirmingDelete && createPortal(
        <ConfirmationModal
          isOpen
          title={confirmingDelete.title}
          message={confirmingDelete.message}
          // Like the chart's dialogs: keeping the chords is the main action,
          // deleting the red one
          confirmText={confirmingDelete.keepChords ? 'Keep Chords' : 'Delete'}
          onConfirm={() => {
            setConfirmingDelete(null);
            (confirmingDelete.keepChords ?? confirmingDelete.remove)();
          }}
          onAltConfirm={confirmingDelete.keepChords ? () => {
            setConfirmingDelete(null);
            confirmingDelete.remove();
          } : undefined}
          altConfirmText="Delete Row"
          onCancel={() => setConfirmingDelete(null)}
        />,
        document.body,
      )}
    </>
  );
};

export default ChartReorderModal;
